import LiveStreamCommentBox from 'components/common/LiveStreamCommentBox';
import cn from 'classnames';
import styles from './LiveStreamComments.module.scss';
import {
  LiveChatType,
  Message,
  Post,
  TipEmotion,
  TippedInfoDetail,
  User,
} from 'common/interfaces/api';
import RoundIcon from 'components/common/RoundIcon';
import Icon from 'components/common/Icon';
import { useEffect, useRef, useState } from 'react';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';
import axios from 'axios';
import { clone, useMediaQuery } from 'common/utils';
import TippedItem from 'components/common/TippedItem';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useQuery } from 'react-query';
import { getMessagesByGroup } from 'service/chat';
import LabelButton from 'components/common/LabelButton';
import classNames from 'classnames';
import debounce from 'common/utils/debounce';
import { useMemo } from 'react';
import { useCallback } from 'react';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';

const MAX_MESSAGES_COUNT = 200;

const LiveStreamComments = ({
  className,
  post,
  tippedList = [],
  tippedCount = 0,
  setOpenTippedList,
  requestToken,
  isLocked,
}: {
  className?: string;
  post: Post;
  tippedList?: TippedInfoDetail[];
  tippedCount?: number;
  setOpenTippedList?: (open: boolean) => void;
  requestToken: string;
  isLocked: boolean;
}) => {
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [isShared, setIsShared] = useState(post.is_shared);
  const showTippingDialog = useGlobalStore((state) => state.showTippingDialog);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const currentUser = useStore((state) => state.currentUser);
  const [onHover, setOnHover] = useState(false);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  // const router = useRouter();
  // const isPostDetail = router.pathname.startsWith('/[username]/posts/[postId]');
  // const postId = router.query.postId as string;
  const setPostToShareByPopup = useGlobalStore(
    (state) => state.setPostToShareByPopup
  );
  const isSmallScreen = useMediaQuery('(max-width: 1200px)');
  const isMobileScreen = useMediaQuery('(max-width: 767px)');
  const maxShowTip = isMobileScreen ? 4 : isSmallScreen ? 6 : 8;
  const refetchTippedListOfPost = useStore(
    (state) => state.refetchTippedListOfPost
  );
  const commentsRef = useRef<HTMLDivElement>();
  const messGroupId = post?.streaming?.message_group?.group_key || '';

  useEffect(() => {
    if (!document.body) return;
    if (
      onHover &&
      commentsRef.current?.scrollHeight > commentsRef.current?.clientHeight
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      disablePageScroll();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      enablePageScroll();
    }
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      enablePageScroll();
    };
  }, [onHover]);
  const debouncedScrollToBottom = useMemo(
    () =>
      debounce(() => {
        if (!commentsRef.current) return;
        commentsRef.current.scrollTop = commentsRef.current.scrollHeight;
      }, 500),
    []
  );

  const socketUrl = `${process.env.NEXT_PUBLIC_WSS_BASE_URL}/chats/start/${messGroupId}?token=${requestToken}`;
  const [messageHistory, setMessageHistory] = useState<Message[]>([]);
  useQuery(
    ['getMessByGroup', messGroupId?.toString()],
    async () => getMessagesByGroup(messGroupId, 1),
    {
      refetchOnWindowFocus: false,
      enabled: isAuthInit && !!currentAuth,
      onSuccess: (data) => {
        const mess = data?.data?.messages || [];
        setMessageHistory(mess.reverse().concat(clone(messageHistory)));
        debouncedScrollToBottom();
      },
    }
  );

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    socketUrl,
    {
      //Will attempt to reconnect on all close events, such as server shutting down
      shouldReconnect: () => true,
    },
    !!requestToken && !!messGroupId
  );

  const showMessages = useCallback(
    (newMessage: Message) => {
      setMessageHistory((prev) => {
        const newMessesList = prev.concat(newMessage);
        if (newMessesList.length > MAX_MESSAGES_COUNT) {
          return newMessesList.slice(-MAX_MESSAGES_COUNT);
        }
        return newMessesList;
      });
      debouncedScrollToBottom();
    },
    [debouncedScrollToBottom]
  );

  const debouncedshowMessages = useMemo(
    () => debounce(showMessages, 10),
    [showMessages]
  );

  useEffect(() => {
    setMessageHistory([]);
  }, [post.id]);

  useEffect(() => {
    if (!refetchTippedListOfPost || !setMessageHistory) return;
    if (lastMessage !== null) {
      try {
        const newMess = JSON.parse(lastMessage.data) as Message;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        debouncedshowMessages(newMess);
        if (newMess.type === LiveChatType.Tip) {
          refetchTippedListOfPost();
        }
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }
  }, [
    debouncedshowMessages,
    lastMessage,
    refetchTippedListOfPost,
    setMessageHistory,
  ]);

  const handleClickSendMessage = (mess: string) => {
    sendJsonMessage({ type: LiveChatType.Chat, message: mess });
  };

  useEffect(() => {
    setIsLiked(post.is_liked);
    setIsShared(post.is_shared);
  }, [post.is_shared, post.is_liked]);

  const handleShareClick = async () => {
    if (!currentAuth) {
      showLogin({});
      return;
    }
    if (isShared) return;

    try {
      await axios.post(`/posts/${post.id}/shares`, {});
      if (!isShared) {
        setIsShared(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleLikeClick = async () => {
    if (currentAuth) {
      const action = isLiked ? 'unlike' : 'like';
      if (isLiked) {
        setIsLiked(false);
      } else {
        setIsLiked(true);
      }
      await axios.post(`/posts/${post.id}/reaction?action=${action}`, {});
    } else {
      showLogin({});
    }
  };

  if (!post || !messGroupId) return <></>;
  return (
    <div className={cn(styles.commentSection, className)}>
      <div className={styles.title}>LIVE CHAT</div>
      {tippedList.length > 0 && (
        <div className={styles.tippedList}>
          {tippedList.slice(0, maxShowTip).map((tippedInfo, index) => {
            return (
              <TippedItem
                className={styles.tippedLabel}
                tippedInfo={tippedInfo}
                key={`tip-${index}`}
              />
            );
          })}
          {tippedCount > maxShowTip && (
            <LabelButton
              text={
                <div className={styles.btnText}>
                  +{tippedCount - maxShowTip}
                </div>
              }
              className={classNames(styles.tippedLabel, styles.loadmoreBtn)}
              type="gray"
              onClick={() => setOpenTippedList && setOpenTippedList(true)}
            />
          )}
        </div>
      )}

      <div
        className={styles.commentsList}
        ref={commentsRef}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
      >
        {messageHistory.map((message, index) => {
          const data = {
            avatar_url: message.avatar_url || message.user_info?.avatar_url,
            message: message.message,
            created_at: message.created_at,
            name: message.name || message.user_info?.name || message.username,
            tip: message.tip || null,
            type: message.type,
          } as Message;

          return (
            <div
              key={`mess-${index}`}
              className={cn(styles.comment, {
                [styles[`tip-${data.tip?.id}`]]: !!data.tip?.id,
              })}
            >
              <div className={styles.leftSide}>
                <RoundIcon
                  user={{ avatar_url: data.avatar_url } as User}
                  width={30}
                  height={30}
                  enableClick={false}
                  emotionSize={15}
                  emotion={
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    data.tip ? TipEmotion[`Tip${data.tip.id}`] : null
                  }
                />
              </div>
              <div className={styles.rightSide}>
                {data.type === LiveChatType.Tip ? (
                  <div className={styles.tipContent}>
                    <span className={styles.userName}>{data.name}</span>
                    <LabelButton
                      text={<span>¥{data.tip?.amount}</span>}
                      className={styles.tipping}
                      type={
                        `tip-${data.tip?.id}` as
                          | 'tip1'
                          | 'tip2'
                          | 'tip3'
                          | 'tip4'
                          | 'tip5'
                      }
                    />
                  </div>
                ) : (
                  <div className={styles.content}>
                    <span className={styles.userName}>{data.name}</span>
                    <span className={styles.commentText}>{data.message}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.actionBox}>
        <div className={styles.postReactions}>
          <div className={styles.action} onClick={() => handleLikeClick()}>
            <Icon name="heart" width={20} height={20} isOn={isLiked} hasOn />
            <div className={styles.numberWrapper}>
              <span className={styles.text}>いいね</span>
            </div>
          </div>
          <div
            className={cn(styles.action, styles.shareMenuTrigger)}
            onClick={() => {
              if (!currentAuth) {
                showLogin({});
                return;
              }
              void handleShareClick();
              setPostToShareByPopup(post);
            }}
          >
            <Icon name="share" width={20} height={20} isOn={isShared} hasOn />
            <div className={styles.numberWrapper}>
              <span className={styles.text}>シェア</span>
            </div>
          </div>
          <div
            className={cn(styles.action, styles.thanks, {
              [styles.disabled]:
                post.user_info?.id === currentUser?.id ||
                !post.user_info?.is_creator ||
                !post.user_info?.is_open_for_subscription,
            })}
            onClick={() => {
              if (post.user_info?.id === currentUser?.id) return;
              showTippingDialog({ post: post, isStreaming: true });
            }}
          >
            <Icon name="thanks-icon" width={20} height={20} />
            <div className={styles.numberWrapper}>
              <span className={styles.text}>ありがとう</span>
            </div>
          </div>
        </div>
        <LiveStreamCommentBox
          sendMessage={handleClickSendMessage}
          isAbleToComment={readyState === ReadyState.OPEN}
          isLocked={isLocked}
          postType={post.type}
          creator={post.user_info}
        />
      </div>
    </div>
  );
};

export default LiveStreamComments;
